import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { Link as RouterLink, useLocation } from 'react-router';
import {
  EventBudget,
  EventResponse,
  EventResponseWithChapters,
  InviteeResponse,
} from 'api';
import { LearnerContributors } from 'api/organisationGroups/organisationGroupsTypes';
import { Alert, TextV2 } from '@insights-ltd/design-library/components';
import {
  dateInPastForZone,
  formatDateFromIsoString,
} from '@insights-ltd/design-library/utils';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { Trans, useTranslation } from 'react-i18next';
import Link from '@mui/material/Link';
import {
  isDFCExperience,
  isExploreExperience,
  isIdtlExperience,
} from 'domain/event';
import { profilesPurchased, profilesReadyToPurchased } from 'domain/invitee';
import { TProfilesCount } from 'pages/Event/types';
import { readyToPurchaseFilter } from 'utils/eventFilters';
import { useAuth } from 'contexts/AuthContext';
import { isRestrictedUser } from 'utils/role';
import { getPreviousProfileInvitees } from 'pages/EventLearners/EventLearners.helper';
import { PurchasedProfilesCard } from './PurchasedProfilesCard';
import AddLearnersCard from './AddLearnersCard';
import AssignChaptersCard from './AssignChaptersCard';
import EvaluatorRemindersCard from './EvaluatorRemindersCard';
import DownloadProfilesCard from './DownloadProfilesCard';
import DownloadTeamWheelCard from './DownloadTeamWheelCard';
import SendInvitesCard from './SendInvitesCard';
import AddLearnersAndContributorsCard from './AddLearnersAndContributorsCard';
import EventWallet from './EventWallet';
import {
  CancelledStyledDiv,
  InfoStyledDiv,
  Notice,
} from './EventOverViewHelper';

type EventOverviewProps = {
  invitees: InviteeResponse[];
  contributorLearners?: LearnerContributors[];
  event: EventResponse;
  readExpiredIsPermitted: boolean;
};

const isBudgetedEvent = (
  event: EventResponse,
): event is EventResponse & { budget: EventBudget } => {
  return event.budget !== undefined && event.budget !== null;
};

const EventOverview = ({
  invitees,
  contributorLearners = [],
  event,
  readExpiredIsPermitted,
}: EventOverviewProps) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const learnerActionsCount = getPreviousProfileInvitees(invitees).length;

  const isIDTL = isIdtlExperience(event.eventType);
  const isDFC = isDFCExperience(event.eventType);
  const isExplore = isExploreExperience(event.eventType);
  const budgetedEvent = isBudgetedEvent(event);

  const eventWithChapters = event as EventResponseWithChapters;
  const isCancelled = event.eventStatus === 'CANCELLED';

  const pastDeadline =
    event.deadlineAt?.length > 0 &&
    dateInPastForZone(event.deadlineAt, event.timezone);
  const pastEndDate = dateInPastForZone(event.endsAt, event.timezone);
  const pastExtendedEndDate = dateInPastForZone(
    event.extendedEndDate,
    event.timezone,
  );

  const isWithinGracePeriod =
    !isCancelled && pastEndDate && !pastExtendedEndDate;

  const isArchived = !isCancelled && pastExtendedEndDate;

  const endDateText = formatDateFromIsoString(event.endsAt, i18n.language);
  const extendedEndDateText = formatDateFromIsoString(
    event.extendedEndDate,
    i18n.language,
  );
  const cancelledDateText = event.cancelledAt
    ? formatDateFromIsoString(event.cancelledAt, i18n.language)
    : null;

  const profilesStatusCount: TProfilesCount = {
    noOfProfilesReadyToPurchase: invitees.filter(profilesReadyToPurchased)
      .length,
    noOfProfilesPurchased: invitees.filter(profilesPurchased).length,
  };

  const learnerStatusCount: TProfilesCount = {
    noOfProfilesReadyToPurchase: contributorLearners.filter(
      readyToPurchaseFilter,
    ).length,
    noOfProfilesPurchased: contributorLearners.filter((contributors) => {
      return contributors.contributors.filter((c) => {
        return (
          c.status !== 'READY_TO_PURCHASE' &&
          c.status !== 'ADDED' &&
          c.status !== 'INVITE_REQUESTED' &&
          c.status !== 'INVITE_SENT'
        );
      }).length;
    }).length,
  };

  const useLearnerCount =
    isDFC && contributorLearners[0]?.contributors.length > 0;

  const profileCost =
    budgetedEvent && eventWithChapters.chapters
      ? eventWithChapters.chapters[0].cost
      : 0;

  const allContributors = useMemo(
    () => contributorLearners.flatMap(({ contributors }) => contributors),
    [contributorLearners],
  );

  const eventStatus = event?.eventStatus;
  const eventBalance = event.budget?.recoverable || 0;
  const potential = event.budget?.potentialSpend || 0;
  const budget = event.budget?.total || 0;
  const preventInvites = budgetedEvent && budget < potential;

  const disableInvites = pastExtendedEndDate || isCancelled || preventInvites;

  const cancelledORExpired =
    isCancelled || preventInvites || pastExtendedEndDate;
  const isRestricted = isRestrictedUser(user?.role);

  const disableAddLearnerCard =
    (pastExtendedEndDate && !readExpiredIsPermitted) || isCancelled;

  const readonlyAddLearnerCard =
    (pastExtendedEndDate || isCancelled) && readExpiredIsPermitted;

  return (
    // TODO: replace with Alert component
    <>
      <Notice
        condition={isArchived}
        text={t('ui.event-management.events.learners.archived.alert2', {
          date: endDateText,
        })}
        Wrapper={InfoStyledDiv}
      />
      <Notice
        condition={isWithinGracePeriod}
        text={t('ui.event-management.events.learners.archived.alert', {
          date: extendedEndDateText,
        })}
        Wrapper={InfoStyledDiv}
      />
      <Notice
        condition={isCancelled}
        text={
          cancelledDateText
            ? t('ui.event-management.events.cancelled-experience.notice.date', {
                date: cancelledDateText,
              })
            : t('ui.event-management.events.cancelled-experience.notice')
        }
        Wrapper={CancelledStyledDiv}
      />
      <Box
        sx={{
          mb: (theme) => theme.spacing(spacingSizeMap.S),
        }}
      >
        <TextV2 component="h1" variant="h2">
          {t('ui.event-management.events.learners.title')}
        </TextV2>
      </Box>
      {budgetedEvent && (
        <>
          <Box
            sx={{
              mb: (theme) => theme.spacing(spacingSizeMap.S),
            }}
          >
            <TextV2 component="h2" variant="h3">
              {t(
                'ui.event-management.events.learners.subtitle.unit-management',
              )}
            </TextV2>
          </Box>
          <EventWallet
            eventId={event.id}
            profileCost={profileCost}
            eventBalance={eventBalance}
            eventStatus={eventStatus}
            pastExtendedEndDate={pastExtendedEndDate}
            isExplore={isExplore}
            budget={event.budget}
          />
        </>
      )}
      {learnerActionsCount > 0 && (
        <Alert
          severity="info"
          sx={{
            '& .MuiAlert-message': {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            },
          }}
        >
          <span>
            <TextV2 variant="body1" component="span" color="textPrimary">
              <Trans
                t={t}
                i18nKey="ui.event-management.events.learners.action-info-message"
                values={{ count: learnerActionsCount }}
                components={{
                  bold: (
                    <TextV2
                      component="span"
                      variant="bodyBold"
                      color="textPrimary"
                    />
                  ),
                }}
              />
            </TextV2>
          </span>
          <Link
            id="manage-learner-action"
            component={RouterLink}
            to={`${pathname}/learners`}
          >
            {t(
              'ui.event-management.events.learners.action-info-message.button',
            )}
          </Link>
        </Alert>
      )}
      <Box
        sx={{
          mb: (theme) => theme.spacing(spacingSizeMap.S),
        }}
      >
        <TextV2 component="h2" variant="h3">
          {t('ui.event-management.events.learners.subtitle.actions')}
        </TextV2>
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SendInvitesCard
            addUnitsWarning={preventInvites}
            invitees={isDFC ? allContributors : invitees}
            event={event}
            disabled={disableInvites}
          />
        </Grid>
        {!isIDTL && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <EvaluatorRemindersCard
              inviteesStatuses={isDFC ? allContributors : invitees}
              pastDeadline={pastDeadline}
              pastExperience={pastEndDate}
              event={event}
              disabled={isCancelled}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6 }}>
          <PurchasedProfilesCard
            event={event}
            profilesStatusCount={
              useLearnerCount ? learnerStatusCount : profilesStatusCount
            }
            disabled={cancelledORExpired}
            isRestricted={isRestricted}
          />
        </Grid>
      </Grid>
      <Box
        sx={{
          my: (theme) => theme.spacing(spacingSizeMap.L),
        }}
      >
        <Divider />
      </Box>
      <Box
        sx={{
          mb: (theme) => theme.spacing(spacingSizeMap.S),
        }}
      >
        <TextV2 component="h2" variant="h3">
          {t('ui.event-management.events.learners.subtitle.event-tasks')}
        </TextV2>
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {isDFC ? (
            <AddLearnersAndContributorsCard
              disabled={!readExpiredIsPermitted}
              hasLearner={contributorLearners.length >= 1}
              pastExtendedEndDate={pastExtendedEndDate}
              learnersCount={contributorLearners.length}
              contributorsCount={allContributors.length}
              isCancelled={isCancelled}
            />
          ) : (
            <AddLearnersCard
              readonly={readonlyAddLearnerCard}
              noOfInvitees={invitees.length}
              disabled={disableAddLearnerCard}
              learnerActionsCount={learnerActionsCount}
            />
          )}
        </Grid>
        {!isIDTL && !isDFC && !isExplore && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <AssignChaptersCard
              invitees={invitees}
              disabled={isCancelled || pastDeadline}
            />
          </Grid>
        )}
        {!isExplore && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <DownloadProfilesCard
              disabled={isCancelled}
              profilesStatusCount={
                useLearnerCount ? learnerStatusCount : profilesStatusCount
              }
            />
          </Grid>
        )}
        {!isDFC && (
          <Grid size={{ xs: 12, sm: 6 }}>
            {!isExplore && (
              <DownloadTeamWheelCard
                noOfProfilesPurchased={
                  profilesStatusCount.noOfProfilesPurchased
                }
                disabled={isCancelled}
              />
            )}
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default EventOverview;
